import { Component, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import type { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../services/auth.service';
import { NavigationService } from '../services/navigation.service';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { UserSession } from '../types/session';
import { SessionService } from '../services/session.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { getApiErrorMessage } from '../services/api-error-message';
import { UiAuthCardComponent } from '../ui/auth-card/auth-card.component';
import { UiFieldComponent } from '../ui/field/field.component';
import { UiInputDirective } from '../ui/input/input.directive';
import { UiButtonDirective } from '../ui/button/button.directive';
import { injectMutation } from '@tanstack/angular-query-experimental';
import type { EmailMfaPendingResponse } from '../generated-api/models';

interface LoginFormValue {
  email?: string;
  password?: string;
}

const DEFAULT_LOGIN_ERROR_MESSAGE = 'Please check your username/password and try again.';
const EMAIL_VALIDATION_ERROR_MESSAGE =
  'We could not validate that email link. Please request a new validation email and try again.';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiAuthCardComponent,
    ReactiveFormsModule,
    FormsModule,
    UiFieldComponent,
    UiInputDirective,
    RouterLink,
    UiButtonDirective,
  ],
})
export class LoginComponent implements OnInit {
  private auth = inject(AuthService);
  private nav = inject(NavigationService);
  sessionService = inject(SessionService);
  private route = inject(ActivatedRoute);

  private readonly loginMutation = injectMutation(() => this.auth.loginMutationOptions());
  private readonly verifyEmailMfaMutation = injectMutation(() => this.auth.verifyEmailMfaMutationOptions());
  private readonly resendEmailMfaMutation = injectMutation(() => this.auth.resendEmailMfaMutationOptions());
  readonly loading = signal(false);
  readonly loginFailed = signal(false);
  readonly loginErrorMessage = signal('');
  loginData: LoginFormValue = {};
  readonly mfaChallenge = signal<EmailMfaPendingResponse | null>(null);
  readonly mfaCode = signal('');
  readonly rememberMeInputId = 'login-remember-me';
  get mfaExpiryMinutes(): number {
    return Math.max(1, Math.ceil((this.mfaChallenge()?.expiresInSeconds ?? 0) / 60));
  }

  ngOnInit() {
    this.nav.title = 'Login to Budgets';
    this.showEmailValidationError();
  }

  loginWithUserPass(form: NgForm) {
    if (form.valid) {
      this.loading.set(true);
      this.loginFailed.set(false);
      this.loginErrorMessage.set('');
      void this.login('userpass', form);
    }
  }

  async login(method: 'userpass', form?: NgForm): Promise<void> {
    if (!form) return;

    try {
      const session = await this.loginMutation.mutateAsync({ method, form });
      if (this.auth.isMfaPending(session)) {
        this.mfaChallenge.set(session);
        this.loading.set(false);
      } else this.finishLogin(session);
    } catch (error) {
      this.onLoginError(form, error as HttpErrorResponse);
    }
  }

  async verifyMfa(): Promise<void> {
    const challenge = this.mfaChallenge();
    if (!challenge) return;
    if (!/^\d{6}$/.test(this.mfaCode())) {
      this.loginFailed.set(true);
      this.loginErrorMessage.set('Enter the six-digit code from your email.');
      return;
    }
    this.loading.set(true);
    this.loginFailed.set(false);
    try {
      this.finishLogin(
        await this.verifyEmailMfaMutation.mutateAsync({
          challengeId: challenge.challengeId,
          code: this.mfaCode(),
        }),
      );
    } catch (error) {
      this.loginFailed.set(true);
      this.loginErrorMessage.set(getApiErrorMessage(error as HttpErrorResponse) ?? 'That code is invalid or expired.');
      this.loading.set(false);
    }
  }

  async resendMfa(): Promise<void> {
    const challenge = this.mfaChallenge();
    if (!challenge) return;
    try {
      await this.resendEmailMfaMutation.mutateAsync(challenge.challengeId);
      this.loginFailed.set(false);
      this.loginErrorMessage.set('');
    } catch (error) {
      this.loginFailed.set(true);
      this.loginErrorMessage.set(getApiErrorMessage(error as HttpErrorResponse) ?? 'Could not resend the code.');
    }
  }

  private finishLogin(session: UserSession) {
    this.sessionService.updateSession(session);
    this.loading.set(false);
    this.redirectOnLogin();
  }

  private redirectOnLogin() {
    const redirectTo = this.route.snapshot.paramMap.get('redirectTo');
    return redirectTo ? this.nav.navigateTo([redirectTo]) : this.nav.goHome();
  }

  private onLoginError(form: NgForm, error?: HttpErrorResponse) {
    form.resetForm({ email: this.getLoginFormValue(form).email });
    this.loginFailed.set(true);
    this.loginErrorMessage.set(error
      ? (getApiErrorMessage(error) ?? DEFAULT_LOGIN_ERROR_MESSAGE)
      : DEFAULT_LOGIN_ERROR_MESSAGE);
    this.loading.set(false);
  }

  private getLoginFormValue(form: NgForm): LoginFormValue {
    const email = form.controls['email']?.value ?? '';
    const password = form.controls['password']?.value ?? '';
    return { email, password };
  }

  private showEmailValidationError() {
    if (this.route.snapshot.queryParamMap?.get('error') !== 'email-validation-failed') return;

    this.loginFailed.set(true);
    this.loginErrorMessage.set(EMAIL_VALIDATION_ERROR_MESSAGE);
  }
}
